/**
 * Minimal router on top of the History API: real URLs (a report has a shareable
 * link) without a dependency. nginx serves index.html for unknown paths (try_files).
 */

export interface Match {
  name: RouteName;
  params: Record<string, string>;
}

export type RouteName = "login" | "search" | "report" | "history" | "docs" | "not-found";

const ROUTES: { name: RouteName; pattern: RegExp; keys: string[] }[] = [
  { name: "search", pattern: /^\/$/, keys: [] },
  { name: "login", pattern: /^\/connexion\/?$/, keys: [] },
  { name: "report", pattern: /^\/rapports\/([^/]+)\/?$/, keys: ["id"] },
  { name: "history", pattern: /^\/historique\/?$/, keys: [] },
  { name: "docs", pattern: /^\/documentation\/?$/, keys: [] },
];

export const PUBLIC_ROUTES: RouteName[] = ["login"];

export function match(pathname: string): Match {
  for (const route of ROUTES) {
    const found = pathname.match(route.pattern);
    if (found) {
      const params = Object.fromEntries(route.keys.map((k, i) => [k, decodeURIComponent(found[i + 1]!)]));
      return { name: route.name, params };
    }
  }
  return { name: "not-found", params: {} };
}

/** Where to go after logging in: only an internal path (no open redirect). */
export function safeNext(search: string): string {
  const next = new URLSearchParams(search).get("suite");
  return next && next.startsWith("/") && !next.startsWith("//") ? next : "/";
}

export function navigate(path: string, { replace = false } = {}): void {
  if (path === location.pathname + location.search) return;
  if (replace) history.replaceState(null, "", path);
  else history.pushState(null, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
  window.scrollTo({ top: 0 });
}

/** Intercepts clicks on internal links, including inside Shadow DOM. */
export function interceptLinks(): void {
  document.addEventListener("click", (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
    const anchor = event.composedPath().find((el): el is HTMLAnchorElement => el instanceof HTMLAnchorElement);
    if (!anchor || anchor.target || anchor.hasAttribute("download") || anchor.origin !== location.origin) return;
    if (anchor.pathname.startsWith("/api/")) return;
    if (anchor.hash && anchor.pathname === location.pathname) return; // in-page anchor
    event.preventDefault();
    navigate(anchor.pathname + anchor.search);
  });
}
